import React, { useState } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
  Typography,
  Paper,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { techSkills, skillsByCategory } from '../../data/techSkills';

interface SkillsAutocompleteProps {
  value?: string[];
  onChange: (skills: string[]) => void;
  error?: boolean;
  helperText?: string;
}

const SkillsAutocomplete: React.FC<SkillsAutocompleteProps> = ({
  value,
  onChange,
  error,
  helperText,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleAddSkill = (skill: string) => {
    if (skill && !(value || []).includes(skill)) {
      onChange([...(value || []), skill]);
      setInputValue('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    onChange((value || []).filter(skill => skill !== skillToRemove));
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      handleAddSkill(inputValue.trim());
    }
  };

  const renderOption = (props: any, option: string) => {
    // Find which category this skill belongs to
    let category = 'Other';
    for (const [cat, skills] of Object.entries(skillsByCategory)) {
      if (skills.includes(option)) {
        category = cat;
        break;
      }
    }

    return (
      <ListItem {...props} sx={{ py: 0.5 }}>
        <ListItemText
          primary={option}
          secondary={category}
          primaryTypographyProps={{ fontSize: '0.9rem' }}
          secondaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
        />
      </ListItem>
    );
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Select from the list or type your own skills (minimum 3 required)
      </Typography>
      
      <Autocomplete
        freeSolo
        options={techSkills}
        value={null}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            handleAddSkill(newValue);
          } else if (newValue) {
            handleAddSkill(newValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Type or select skills (e.g., React, Python, AWS)"
            onKeyPress={handleKeyPress}
            error={error}
            helperText={helperText}
            size="small"
          />
        )}
        renderOption={renderOption}
        renderTags={() => null} // Don't render tags in the input
        filterOptions={(options, { inputValue }) => {
          const filtered = options.filter(option =>
            option.toLowerCase().includes(inputValue.toLowerCase())
          );
          return filtered.slice(0, 10); // Limit to 10 suggestions
        }}
        sx={{ mb: 2 }}
      />

      {/* Display selected skills as chips */}
      <Box sx={{ mt: 2 }}>
        <Typography 
          variant="body2" 
          color={(value || []).length >= 3 ? "success.main" : "text.secondary"} 
          gutterBottom
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: (value || []).length >= 3 ? 'bold' : 'normal'
          }}
        >
          Selected Skills ({(value || []).length}/3 minimum)
          {(value || []).length >= 3 && (
            <span style={{ color: 'green' }}>✓</span>
          )}
        </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(value || []).map((skill, index) => (
              <Chip
                key={index}
                label={skill}
                onDelete={() => handleRemoveSkill(skill)}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ 
                  '& .MuiChip-deleteIcon': {
                    fontSize: '1rem',
                  }
                }}
              />
            ))}
          </Box>
        </Box>

      {/* Skills categories hint */}
      <Paper elevation={1} sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          <strong>Available Categories:</strong> Programming Languages, Web Development, Backend & Databases, Cloud & DevOps, Mobile Development, Data Science & AI, Blockchain & Web3, Testing & QA, Tools & Platforms, UI/UX & Design, Project Management, Emerging Tech
        </Typography>
      </Paper>
    </Box>
  );
};

export default SkillsAutocomplete; 